import { NetworkType, SUPPORTED_NETWORKS } from '@mls-wallet/shared-types';
import { SignClientTypes, SessionTypes } from '@walletconnect/types';
import {
  WalletConnectSessionRequestEvent,
  WalletConnectDappData,
  WalletConnectTransactionData,
  WalletConnectTransactionError,
} from './types';
import SignClient from '@walletconnect/sign-client';
import {
  formatJsonRpcError,
  formatJsonRpcResult,
} from '@walletconnect/jsonrpc-utils';
import { getSdkError } from '@walletconnect/utils';

const WALLETCONNECT_PROJECT_ID = 'b57c03cd9481311fac0460e457fd4be6';
const WALLETCONNECT_SESSION_TTL_MS = 15 * 60 * 1000;
const WALLETCONNECT_ACTIVITY_STORAGE_KEY =
  'walletconnect-session-last-activity-ms';

type WalletConnectSignClient = Awaited<ReturnType<typeof SignClient.init>>;

type SessionProposalHandler = (
  dappData: WalletConnectDappData[]
) => Promise<void> | void;

type SessionRequestHandler = (
  request: SignClientTypes.EventArguments['session_request'],
  transactionData?: WalletConnectTransactionData,
  transactionError?: WalletConnectTransactionError
) => Promise<void> | void;

type SessionDeleteHandler = (
  dappData: WalletConnectDappData[]
) => Promise<void> | void;

type InitializeClientParams = {
  accountAddress: string;
  networkId: NetworkType;
  onSessionProposal: SessionProposalHandler;
  onSessionRequest: SessionRequestHandler;
  onSessionDelete: SessionDeleteHandler;
};

/**
 * Parses an EIP-155 chain string like `eip155:1` into a numeric chain id.
 */
function parseEip155ChainId(chain: string): number | null {
  // Case: value is not an EIP-155 chain string.
  if (!chain.startsWith('eip155:')) {
    return null;
  }

  const parsedValue = parseInt(chain.replace('eip155:', ''), 10);
  return Number.isNaN(parsedValue) ? null : parsedValue;
}

class WalletConnectRuntime {
  private static instance: WalletConnectRuntime | null = null;
  private clientPromise: Promise<WalletConnectSignClient> | null = null;
  private listenersRegistered: boolean = false;
  private runtimeContext: InitializeClientParams | null = null;
  private signClient: WalletConnectSignClient | null = null;

  /**
   * Intentionally empty private constructor.
   *
   * This class is a singleton: only `getInstance()` can create it.
   * We keep the constructor side-effect free to avoid eager WalletConnect
   * initialization during module import. Actual client initialization happens
   * lazily in `ensureClient()`, which gives us explicit lifecycle control and
   * avoids unnecessary startup work when WalletConnect is never used.
   */
  private constructor() {}

  /**
   * Returns the singleton runtime instance, creating it lazily on first use.
   */
  public static getInstance(): WalletConnectRuntime {
    // Case: singleton not created yet; create it now.
    if (!WalletConnectRuntime.instance) {
      WalletConnectRuntime.instance = new WalletConnectRuntime();
    }
    return WalletConnectRuntime.instance;
  }

  /**
   * Sets the active runtime context and ensures listeners are registered once.
   */
  public async initialize(params: InitializeClientParams): Promise<void> {
    this.runtimeContext = params;
    const client = await this.ensureClient();
    this.registerListeners(client);
    await this.pruneExpiredSessions();
  }

  /**
   * Pairs this wallet runtime with a dApp using a WalletConnect URI.
   */
  public async pair(uri: string): Promise<void> {
    const client = await this.ensureClient();
    await client.pair({ uri });
  }

  /**
   * Responds to a WalletConnect request with a successful JSON-RPC result.
   */
  public async respondSuccess(
    request: WalletConnectSessionRequestEvent,
    result: string = '0x'
  ): Promise<void> {
    const client = await this.ensureClient();
    await client.respond({
      topic: request.topic,
      response: formatJsonRpcResult(request.id, result),
    });
    this.markTopicActivity(request.topic);
  }

  /**
   * Responds to a WalletConnect request with a JSON-RPC error.
   */
  public async respondError(
    request: WalletConnectSessionRequestEvent,
    message: string
  ): Promise<void> {
    const client = await this.ensureClient();
    await client.respond({
      topic: request.topic,
      response: formatJsonRpcError(request.id, message),
    });
    this.markTopicActivity(request.topic);
  }

  /**
   * Disconnects an active WalletConnect session by topic.
   */
  public async disconnect(topic: string): Promise<void> {
    const client = await this.ensureClient();
    await client.disconnect({
      topic,
      reason: getSdkError('USER_DISCONNECTED'),
    });
    this.clearTopicActivity(topic);
  }

  /**
   * Returns connected dApp metadata derived from active sessions.
   */
  public async getExistingDappData(): Promise<WalletConnectDappData[]> {
    const client = await this.ensureClient();
    await this.pruneExpiredSessions();
    const sessions = this.getSessionsForActiveAccount(client.session.getAll());
    return this.mapSessionData(sessions);
  }

  /**
   * Prunes sessions that exceeded the inactivity TTL.
   */
  public async pruneExpiredSessions(
    ttlMs: number = WALLETCONNECT_SESSION_TTL_MS
  ): Promise<void> {
    const client = await this.ensureClient();
    const nowMs = Date.now();
    const activityByTopic = this.getTopicActivityMap();
    const sessions = client.session.getAll();

    // Iterate over all sessions and disconnect expired sessions
    await Promise.all(
      sessions.map(async (session) => {
        const lastActivityMs = activityByTopic[session.topic];
        const isExpired =
          typeof lastActivityMs !== 'number' || nowMs - lastActivityMs > ttlMs;

        // Case: session is not expired, skip pruning
        if (!isExpired) {
          return;
        }

        try {
          await client.disconnect({
            topic: session.topic,
            reason: getSdkError('USER_DISCONNECTED'),
          });
        } catch {
          // Session may already be closed; continue pruning expired sessions.
        } finally {
          this.clearTopicActivity(session.topic);
        }
      })
    );
  }

  /**
   * Lazily initializes and memoizes SignClient, returning the shared instance.
   */
  private async ensureClient(): Promise<WalletConnectSignClient> {
    // Case: SignClient init has not started yet; start once.
    if (!this.clientPromise) {
      this.clientPromise = SignClient.init({
        projectId: WALLETCONNECT_PROJECT_ID,
        metadata: {
          description: 'MLS Wallet',
          url: 'https://mls.onchainden.com',
          icons: ['https://mls.onchainden.com/favicon.ico'],
          name: 'MLS Wallet',
        },
      }).catch((error) => {
        this.clientPromise = null;
        this.signClient = null;
        throw error;
      });
    }

    // Case: resolved SignClient instance not memoized yet.
    if (!this.signClient) {
      this.signClient = await this.clientPromise;
    }

    return this.signClient;
  }

  /**
   * Registers WalletConnect event listeners exactly once.
   */
  private registerListeners(client: WalletConnectSignClient): void {
    // Case: listeners are already attached; skip duplicate registration.
    if (this.listenersRegistered) {
      return;
    }
    this.listenersRegistered = true;

    client.on('session_proposal', async (event) => {
      await this.handleSessionProposal(event);
    });

    client.on('session_request', async (event) => {
      await this.handleSessionRequest(event);
    });

    client.on('session_delete', async (event) => {
      await this.handleSessionDelete(event);
    });
  }

  /**
   * Approves incoming session proposals using supported/requested namespaces.
   */
  private async handleSessionProposal(
    event: SignClientTypes.EventArguments['session_proposal']
  ): Promise<void> {
    const currentContext = this.runtimeContext;
    // Case: runtime has no active context; ignore proposal.
    if (!currentContext) {
      return;
    }
    const signClient = await this.ensureClient();

    const requiredMethods =
      event.params.requiredNamespaces.eip155?.methods ?? [];
    const optionalMethods =
      event.params.optionalNamespaces.eip155?.methods ?? [];
    const requiredEvents = event.params.requiredNamespaces.eip155?.events ?? [];
    const optionalEvents = event.params.optionalNamespaces.eip155?.events ?? [];

    const primaryNetworkId = Number(currentContext.networkId);
    const requestedChainIds = [
      ...(event.params.requiredNamespaces.eip155?.chains ?? []),
      ...(event.params.optionalNamespaces.eip155?.chains ?? []),
    ]
      .map((chain) => parseEip155ChainId(chain))
      .filter((chainId): chainId is number => chainId !== null);

    // Include selected network, supported networks, and dApp-requested chains.
    // This avoids failing session negotiation because of network support checks.
    const orderedNetworkIds = Array.from(
      new Set([
        primaryNetworkId,
        ...SUPPORTED_NETWORKS.map(Number),
        ...requestedChainIds,
      ])
    );

    const accounts = orderedNetworkIds.map(
      (chainId) => `eip155:${chainId}:${currentContext.accountAddress}`
    );

    const approvedSession = await signClient.approve({
      id: event.id,
      namespaces: {
        eip155: {
          accounts,
          methods: [...requiredMethods, ...optionalMethods],
          events: [...requiredEvents, ...optionalEvents],
        },
      },
    });

    await approvedSession.acknowledged();
    this.markTopicActivity(approvedSession.topic);
    await currentContext.onSessionProposal(
      this.mapSessionData(signClient.session.getAll())
    );
  }

  /**
   * Handles incoming session requests and forwards transaction imports.
   */
  private async handleSessionRequest(
    event: SignClientTypes.EventArguments['session_request']
  ): Promise<void> {
    const currentContext = this.runtimeContext;
    // Case: runtime has no active context; reply with explicit error.
    if (!currentContext) {
      await this.respondError(
        { id: event.id, topic: event.topic },
        'WalletConnect runtime context is unavailable'
      );
      return;
    }
    const signClient = await this.ensureClient();
    this.markTopicActivity(event.topic);

    try {
      const rpcMethod = event.params.request.method;
      const rpcParams = event.params.request.params as Array<
        Record<string, string> | undefined
      >;

      // Case: chain switch/add requests are accepted as no-op in V1.
      if (
        rpcMethod === 'wallet_addEthereumChain' ||
        rpcMethod === 'wallet_switchEthereumChain'
      ) {
        await signClient.respond({
          topic: event.topic,
          response: formatJsonRpcResult(event.id, null),
        });
        return;
      }

      // Case: unsupported method.
      if (rpcMethod !== 'eth_sendTransaction') {
        currentContext.onSessionRequest(
          event,
          undefined,
          WalletConnectTransactionError.UNSUPPORTED_METHOD
        );
        await this.respondError(
          { id: event.id, topic: event.topic },
          'Unsupported WalletConnect method'
        );
        return;
      }

      const transactionRequest = rpcParams[0];
      // Case: malformed transaction payload.
      if (!transactionRequest?.to || !transactionRequest?.from) {
        currentContext.onSessionRequest(
          event,
          undefined,
          WalletConnectTransactionError.UNSUPPORTED_METHOD
        );
        await this.respondError(
          { id: event.id, topic: event.topic },
          'Invalid transaction request payload'
        );
        return;
      }

      // Case: request sender does not match connected account.
      if (
        transactionRequest.from.toLowerCase() !==
        currentContext.accountAddress.toLowerCase()
      ) {
        currentContext.onSessionRequest(
          event,
          undefined,
          WalletConnectTransactionError.WRONG_ACCOUNT
        );
        await this.respondError(
          { id: event.id, topic: event.topic },
          'Request from address does not match connected wallet account'
        );
        return;
      }

      const transactionData: WalletConnectTransactionData = {
        topic: event.topic,
        to: transactionRequest.to,
        data: transactionRequest.data ?? '0x',
        value: transactionRequest.value ?? '0x0',
      };

      // Case: native token sends are intentionally unsupported in this flow.
      // Native sends are token transfers; walletconnect is for contract interactions
      if (transactionData.data === '0x') {
        currentContext.onSessionRequest(
          event,
          transactionData,
          WalletConnectTransactionError.NATIVE_SEND_UNSUPPORTED
        );
        await this.respondError(
          { id: event.id, topic: event.topic },
          'Native token send is unsupported in this WalletConnect flow'
        );
        return;
      }

      currentContext.onSessionRequest(event, transactionData);
    } catch {
      // Case: unexpected handler failure.
      await this.respondError(
        { id: event.id, topic: event.topic },
        'Wallet failed to process WalletConnect request'
      );
    }
  }

  /**
   * Handles session deletion by emitting refreshed dApp connection state.
   */
  private async handleSessionDelete(
    event: SignClientTypes.EventArguments['session_delete']
  ): Promise<void> {
    const currentContext = this.runtimeContext;
    // Case: runtime has no active context.
    if (!currentContext) {
      return;
    }
    this.clearTopicActivity(event.topic);
    await currentContext.onSessionDelete(await this.getExistingDappData());
  }

  /**
   * Converts WalletConnect sessions to UI dApp metadata.
   */
  private mapSessionData(
    sessions: SessionTypes.Struct[]
  ): WalletConnectDappData[] {
    const scopedSessions = this.getSessionsForActiveAccount(sessions);
    return scopedSessions.map((session) => {
      const imageUri = session.peer.metadata.icons[0];
      return {
        ...(session.peer.metadata.name
          ? { name: session.peer.metadata.name }
          : {}),
        ...(session.peer.metadata.description
          ? { description: session.peer.metadata.description }
          : {}),
        ...(imageUri ? { imageUri } : {}),
        ...(session.peer.metadata.url
          ? { dappUri: session.peer.metadata.url }
          : {}),
        topic: session.topic,
      };
    });
  }

  /**
   * Returns only sessions associated with the active runtime account.
   */
  private getSessionsForActiveAccount(
    sessions: SessionTypes.Struct[]
  ): SessionTypes.Struct[] {
    const accountAddress = this.runtimeContext?.accountAddress;

    // Case: runtime context is not initialized; skip account scoping.
    if (!accountAddress) {
      return sessions;
    }

    const accountAddressLower = accountAddress.toLowerCase();
    return sessions.filter((session) =>
      session.namespaces.eip155?.accounts.some((account) =>
        account.toLowerCase().endsWith(`:${accountAddressLower}`)
      )
    );
  }

  /**
   * Marks a session topic as active now.
   */
  private markTopicActivity(topic: string): void {
    const activityByTopic = this.getTopicActivityMap();
    activityByTopic[topic] = Date.now();
    this.saveTopicActivityMap(activityByTopic);
  }

  /**
   * Removes saved activity metadata for a session topic.
   */
  private clearTopicActivity(topic: string): void {
    const activityByTopic = this.getTopicActivityMap();
    if (!(topic in activityByTopic)) {
      return;
    }
    delete activityByTopic[topic];
    this.saveTopicActivityMap(activityByTopic);
  }

  /**
   * Reads the stored per-topic activity map from localStorage.
   */
  private getTopicActivityMap(): Record<string, number> {
    if (typeof window === 'undefined') {
      return {};
    }

    try {
      const rawValue = window.localStorage.getItem(
        WALLETCONNECT_ACTIVITY_STORAGE_KEY
      );
      if (!rawValue) {
        return {};
      }

      const parsedValue = JSON.parse(rawValue) as Record<string, unknown>;
      return Object.fromEntries(
        Object.entries(parsedValue).filter(
          (entry): entry is [string, number] =>
            typeof entry[0] === 'string' && typeof entry[1] === 'number'
        )
      );
    } catch {
      return {};
    }
  }

  /**
   * Writes the per-topic activity map to localStorage.
   */
  private saveTopicActivityMap(activityByTopic: Record<string, number>): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(
      WALLETCONNECT_ACTIVITY_STORAGE_KEY,
      JSON.stringify(activityByTopic)
    );
  }
}

/**
 * Exported singleton runtime instance for app-wide use.
 */
export const walletConnectRuntime = WalletConnectRuntime.getInstance();
